import React from 'react';
import { getContext, useCombinedRefs, getEasing, getDuration } from 'common';
import { SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_DECELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { useTransition, animated } from 'react-spring';
import {
  getOverlayStyle,
} from 'components/MaskStyle';
import Modal from 'react-modal';
import Coachmark from './Coachmark';
import Styled from './styles';

// overriding react-modal styles since modal styles need to be animated
const OVERLAY_STYLE = { background: 'transparent', zIndex: 30 };
const CONTENT_STYLE = {
  position: 'absolute' as const,
  top: 'auto',
  left: 'auto',
  right: 'auto',
  bottom: 'auto',
  border: 0,
  background: 'transparent',
  borderRadius: 0,
  outline: 'none',
  padding: 0,
  height: '100%',
  width: '100%',
  overFlow: 'hidden'
};
const DEFAULT_ANIMATION = disableAnimation => ({
  from: { opacity: 0 },
  enter: { opacity: 1 },
  leave: { opacity: 0 },
  config: {
    duration: getDuration(SPEED_SLOW),
    easing: getEasing(TRANSITION_DECELERATE)
  },
  immediate: disableAnimation,
});

export interface CoachmarksProps extends React.ComponentPropsWithRef<'div'> {
  /** Coachmarks content is placed here. See demo example and sub-components for suggested composition. */
  children: React.ReactNode;
  /** Toggles overlay visiblity. */
  overlay?: boolean;
  /** Toggles Coachmarks visibility. */
  show?: boolean;
  /** Pass in modal props here to override react-modal api. See http://reactcommunity.org/react-modal/ */
  modalProps?: Record<string, any>;
  /** Control animation config here. This uses React-spring transitions config. See https://react-spring.io for more information. */
  animationProps?: Record<string, any>;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
}

/** Coachmarks container, place individual coachmarks here.
 * All additional props will be forwarded to the coachmarks container element.
*/
const Coachmarks = React.forwardRef<HTMLElement, CoachmarksProps>((props: CoachmarksProps, ref) =>
  getContext(({ RTL, disableAnimation }) => {
    const containerRef = React.useRef(null);
    const combinedRef = useCombinedRefs(ref, containerRef);
    const transitions = useTransition(props.show, null, DEFAULT_ANIMATION(props.disableAnimation || disableAnimation));
    return (
      <Modal
        isOpen={props.show}
        style={{ overlay: OVERLAY_STYLE, content: CONTENT_STYLE }}
        shouldFocusAfterRender={false}
        ariaHideApp={false}
        bodyOpenClassName="ccfk-modal-open"
        shouldCloseOnEsc
        {...props.modalProps}
      >
        {transitions.map(({ item, key, props: styles }) =>
          item && (
          <Styled.CoachmarkOverlay key={key} as={animated.div} style={styles} $overlay={props.overlay && getOverlayStyle()}>
            <Styled.Coachmarks $rtl={RTL} ref={combinedRef} {...props}>
              {props.show && props.children}
            </Styled.Coachmarks>
          </Styled.CoachmarkOverlay>
          ))}
      </Modal>
    );
  }));

Coachmarks.displayName = 'Coachmarks';
Coachmarks.defaultProps = {
  overlay: false,
  modalProps: {},
  show: false,
  animationProps: undefined,
  disableAnimation: undefined,
};

/* Exports */
export { Coachmarks as default, Coachmark };
